import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import AuditFieldwork from "./AuditFieldwork";
import AuditCipSeniorManagers, { NERC_FRAMEWORK } from "./AuditCipSeniorManagers";
import AuditReports, { AuditReportsAwaitingApproval } from "./AuditReports";

// Security Insights → Audits (Audit Management, 5A step 7): audit engagements of a framework and
// their teams. The server decides who sees what (managers every engagement, a lead or team member
// their own) and who may change it (the engagement's canManage); the screen only mirrors that.

const BASE = "/security-insights/audits";

export const STATUS_STYLE = {
    Planning: "bg-gray-50 text-gray-700 border-gray-200",
    Fieldwork: "bg-blue-50 text-blue-700 border-blue-200",
    Review: "bg-amber-50 text-amber-700 border-amber-200",
    Reporting: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Closed: "bg-green-50 text-green-700 border-green-200",
};

const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : "");
const showDate = (iso) => {
    const d = iso ? new Date(iso) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : "—";
};
const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

// The form's starting values: an engagement being edited, else a new one for the first framework.
export const emptyForm = (frameworks, currentUserId) => ({
    name: "", framework: frameworks[0] || "", scopeMode: "All", scopeRequirementIds: [],
    periodFrom: "", periodTo: "", dueDate: "", leadUserId: currentUserId ?? "",
});
export const formOf = (e) => ({
    name: e.name, framework: e.framework, scopeMode: e.scopeMode, scopeRequirementIds: e.scopeRequirementIds || [],
    periodFrom: dateOnly(e.periodFrom), periodTo: dateOnly(e.periodTo), dueDate: dateOnly(e.dueDate), leadUserId: e.leadUserId,
});
export const requestOf = (form) => ({
    name: form.name, framework: form.framework, scopeMode: form.scopeMode,
    scopeRequirementIds: form.scopeMode === "Selected" ? form.scopeRequirementIds : null,
    periodFrom: form.periodFrom, periodTo: form.periodTo, dueDate: form.dueDate || null,
    leadUserId: Number(form.leadUserId),
});

// canViewRequirements: org-wide GRC access (useRBAC canViewAllGrcData); without it the requirement
// list is not read (the server answers 403) and a Selected scope cannot be chosen from it.
const EngagementForm = ({ initial, frameworks, members, scopeLocked, canViewRequirements = false, onSave, onCancel }) => {
    const apiCall = useApi();
    const [form, setForm] = useState(initial);
    const [requirements, setRequirements] = useState({ loading: false, items: [] });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    // The requirement list to choose from, for a Selected scope.
    useEffect(() => {
        if (!canViewRequirements || form.scopeMode !== "Selected" || !form.framework) return;
        let alive = true;
        setRequirements({ loading: true, items: [] });
        apiCall("GET", `/security-insights/framework-requirements?framework=${encodeURIComponent(form.framework)}`)
            .then(d => alive && setRequirements({ loading: false, items: d?.requirements || [] }))
            .catch(() => alive && setRequirements({ loading: false, items: [] }));
        return () => { alive = false; };
    }, [apiCall, canViewRequirements, form.framework, form.scopeMode]);

    const toggle = (id) => set({
        scopeRequirementIds: form.scopeRequirementIds.includes(id)
            ? form.scopeRequirementIds.filter(x => x !== id)
            : [...form.scopeRequirementIds, id],
    });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await onSave(requestOf(form));
        } catch (err) {
            setError(messageOf(err, "Could not save the engagement. Try again."));
        } finally {
            setSaving(false);
        }
    };

    const leadOptions = members.some(m => m.id === Number(form.leadUserId)) || !form.leadUserId
        ? members
        : [...members, { id: Number(form.leadUserId), fullName: "Current lead" }];

    return (
        <form onSubmit={submit} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="grc-audit-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <label className="flex flex-col gap-1">Name
                    <input value={form.name} onChange={e => set({ name: e.target.value })} maxLength={200} required
                        className="border border-gray-200 rounded px-2 py-1.5" />
                </label>
                <label className="flex flex-col gap-1">Framework
                    <select value={form.framework} disabled={scopeLocked} onChange={e => set({ framework: e.target.value, scopeRequirementIds: [] })}
                        className="border border-gray-200 rounded px-2 py-1.5 bg-white">
                        {frameworks.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                </label>
                <label className="flex flex-col gap-1">Period from
                    <input type="date" value={form.periodFrom} onChange={e => set({ periodFrom: e.target.value })} required
                        className="border border-gray-200 rounded px-2 py-1.5" />
                </label>
                <label className="flex flex-col gap-1">Period to
                    <input type="date" value={form.periodTo} onChange={e => set({ periodTo: e.target.value })} required
                        className="border border-gray-200 rounded px-2 py-1.5" />
                </label>
                <label className="flex flex-col gap-1">Due date (optional)
                    <input type="date" value={form.dueDate} onChange={e => set({ dueDate: e.target.value })}
                        className="border border-gray-200 rounded px-2 py-1.5" />
                </label>
                <label className="flex flex-col gap-1">Audit lead
                    <select value={form.leadUserId} onChange={e => set({ leadUserId: e.target.value })} required
                        className="border border-gray-200 rounded px-2 py-1.5 bg-white">
                        <option value="">Choose the lead</option>
                        {leadOptions.map(m => <option key={m.id} value={m.id}>{m.fullName}</option>)}
                    </select>
                </label>
            </div>

            <fieldset className="text-xs" disabled={scopeLocked}>
                <legend className="font-semibold text-gray-600 mb-1">Scope</legend>
                {scopeLocked && <div className="text-gray-500 mb-1">The scope is fixed once fieldwork starts.</div>}
                <label className="mr-4"><input type="radio" name="scope" checked={form.scopeMode === "All"} onChange={() => set({ scopeMode: "All" })} /> Every applicable requirement</label>
                <label><input type="radio" name="scope" checked={form.scopeMode === "Selected"} onChange={() => set({ scopeMode: "Selected" })} /> Chosen requirements</label>
                {form.scopeMode === "Selected" && (
                    <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded p-2" data-testid="grc-audit-scope-picker">
                        {!canViewRequirements ? <div className="text-gray-400" data-testid="grc-audit-requirements-restricted">Choosing requirements needs organization-wide access.</div>
                            : requirements.loading ? <div className="text-gray-400">Loading requirements…</div>
                            : requirements.items.length === 0 ? <div className="text-gray-400">No requirement list for this framework.</div>
                            : requirements.items.map(r => (
                                <label key={r.id} className="flex items-start gap-2 py-0.5">
                                    <input type="checkbox" checked={form.scopeRequirementIds.includes(r.id)} onChange={() => toggle(r.id)} />
                                    <span><span className="font-semibold">{r.displayId}</span> {r.title}</span>
                                </label>
                            ))}
                        <div className="text-gray-500 mt-1">{form.scopeRequirementIds.length} chosen</div>
                    </div>
                )}
            </fieldset>

            {error && <div className="text-xs text-red-600" role="alert">{error}</div>}
            <div className="flex gap-2">
                <button type="submit" disabled={saving} className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
                    {saving ? "Saving…" : "Save"}
                </button>
                <button type="button" onClick={onCancel} className="text-xs px-3 py-1.5 rounded border border-gray-200">Cancel</button>
            </div>
        </form>
    );
};

const TeamSection = ({ engagement, members, onChange }) => {
    const [userId, setUserId] = useState("");
    const [role, setRole] = useState("Preparer");
    const [error, setError] = useState(null);
    const run = async (body) => {
        setError(null);
        try { await onChange(body); } catch (err) { setError(messageOf(err, "Could not change the team. Try again.")); }
    };
    return (
        <div className="text-xs" data-testid="grc-audit-team">
            <div className="font-semibold text-gray-600 mb-1">Team</div>
            {engagement.team.length === 0
                ? <div className="text-gray-400 mb-1">No preparer or reviewer yet.</div>
                : (
                    <ul className="space-y-1 mb-2">
                        {engagement.team.map(t => (
                            <li key={`${t.userId}-${t.role}`} className="flex items-center gap-2">
                                <span className="text-gray-800">{t.displayName}</span>
                                <span className="text-gray-500">{t.role}</span>
                                {t.left && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">No longer a member</span>}
                                {engagement.canManage && engagement.status !== "Closed" && (
                                    <button type="button" onClick={() => run({ userId: t.userId, role: t.role, member: false })}
                                        className="text-gray-500 hover:text-red-600 hover:underline">Remove</button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            {engagement.canManage && engagement.status !== "Closed" && (
                <div className="flex flex-wrap items-center gap-2">
                    <select aria-label="Team member" value={userId} onChange={e => setUserId(e.target.value)} className="border border-gray-200 rounded px-1.5 py-0.5 bg-white">
                        <option value="">Choose a person</option>
                        {members.map(m => <option key={m.id} value={m.id}>{m.fullName}</option>)}
                    </select>
                    <select aria-label="Team role" value={role} onChange={e => setRole(e.target.value)} className="border border-gray-200 rounded px-1.5 py-0.5 bg-white">
                        <option value="Preparer">Preparer</option>
                        <option value="Reviewer">Reviewer</option>
                    </select>
                    <button type="button" disabled={!userId} onClick={() => run({ userId: Number(userId), role, member: true }).then(() => setUserId(""))}
                        className="px-2 py-0.5 border border-indigo-200 rounded text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">Add</button>
                </div>
            )}
            {error && <div className="text-red-600 mt-1" role="alert">{error}</div>}
        </div>
    );
};

// canCreate: a Security Insights manager (may start an audit). hideWhenEmpty: render nothing when the
// person has no audit (the evidence-only page shows the section only to someone on an audit).
// canViewRequirements: org-wide GRC access (useRBAC canViewAllGrcData), passed to the form and fieldwork.
const GrcAuditsTab = ({ frameworks = [], canCreate = false, currentUserId = null, hideWhenEmpty = false, evidenceRequestsEnabled = false, canViewRequirements = false }) => {
    const apiCall = useApi();
    const [state, setState] = useState({ loading: true, failed: false, items: [] });
    const [members, setMembers] = useState([]);
    const [people, setPeople] = useState({}); // engagement id → the members its lead may pick from
    const [mode, setMode] = useState(null); // null | "new" | { edit: engagement }
    const [openId, setOpenId] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => apiCall("GET", BASE)
        .then(items => setState({ loading: false, failed: false, items: Array.isArray(items) ? items : [] }))
        .catch(() => setState(s => ({ ...s, loading: false, failed: true }))), [apiCall]);
    useEffect(() => { load(); }, [load]);
    // A manager picks the lead of a new audit from the organization's members.
    useEffect(() => {
        if (!canCreate) return;
        apiCall("GET", "/security-insights/policy-docs/members")
            .then(list => setMembers(Array.isArray(list) ? list : []))
            .catch(() => setMembers([]));
    }, [apiCall, canCreate]);

    // Whoever may manage an opened audit (its lead too) picks people from that audit's own list.
    const opened = state.items.find(e => e.id === openId);
    useEffect(() => {
        if (!opened?.canManage || people[opened.id]) return;
        apiCall("GET", `${BASE}/${opened.id}/people`)
            .then(list => setPeople(p => ({ ...p, [opened.id]: Array.isArray(list) ? list : [] })))
            .catch(() => setPeople(p => ({ ...p, [opened.id]: [] })));
    }, [apiCall, opened, people]);
    const peopleOf = (id) => people[id] || members;

    const frameworkNames = useMemo(() => [...new Set(frameworks)], [frameworks]);

    const create = async (body) => {
        const created = await apiCall("POST", BASE, body);
        setMode(null);
        setOpenId(created?.id ?? null);
        await load();
    };
    const update = (id) => async (body) => {
        await apiCall("PUT", `${BASE}/${id}`, body);
        setMode(null);
        await load();
    };
    const remove = async (engagement) => {
        setError(null);
        try {
            await apiCall("DELETE", `${BASE}/${engagement.id}`);
            setOpenId(null);
            await load();
        } catch (err) {
            setError(messageOf(err, "Could not delete the engagement."));
        }
    };
    const changeTeam = (id) => async (body) => {
        await apiCall("PUT", `${BASE}/${id}/team`, body);
        await load();
    };

    if (mode === "new")
        return <EngagementForm initial={emptyForm(frameworkNames, currentUserId)} frameworks={frameworkNames} members={members}
            scopeLocked={false} canViewRequirements={canViewRequirements} onSave={create} onCancel={() => setMode(null)} />;
    if (mode?.edit)
        return <EngagementForm initial={formOf(mode.edit)} frameworks={[...new Set([mode.edit.framework, ...frameworkNames])]} members={peopleOf(mode.edit.id)}
            scopeLocked={mode.edit.status !== "Planning"} canViewRequirements={canViewRequirements} onSave={update(mode.edit.id)} onCancel={() => setMode(null)} />;

    // A CIP Senior Manager or delegate may be on no audit's team: their approvals show either way.
    if (hideWhenEmpty && !state.loading && state.items.length === 0) return <AuditReportsAwaitingApproval />;

    return (
        <div className="space-y-3">
        <AuditReportsAwaitingApproval />
        <div className="bg-white border border-gray-200 rounded-lg" data-testid="grc-audits-tab">
            <div className="p-3 border-b border-gray-200 flex items-center gap-2">
                <div className="mr-auto">
                    <div className="text-sm font-semibold text-gray-800">Audits ({state.items.length})</div>
                    <div className="text-xs text-gray-500">Audits of a framework: scope, period, lead and team.</div>
                </div>
                {canCreate && (
                    <button type="button" onClick={() => setMode("new")} disabled={frameworkNames.length === 0}
                        className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">New audit</button>
                )}
            </div>
            {error && <div className="px-3 pt-2 text-xs text-red-600" role="alert">{error}</div>}
            {state.loading ? <div className="p-6 text-center text-xs text-gray-400">Loading audits…</div>
                : state.failed ? <div className="p-6 text-center text-xs text-red-600" role="alert">Could not load the audits. Try again.</div>
                : state.items.length === 0 ? <div className="p-6 text-center text-xs text-gray-400">No audit yet.</div>
                : (
                    <ul className="divide-y divide-gray-100">
                        {state.items.map(e => (
                            <li key={e.id} data-testid={`grc-audit-${e.id}`}>
                                <button type="button" onClick={() => setOpenId(openId === e.id ? null : e.id)} aria-expanded={openId === e.id}
                                    className="w-full text-left px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 hover:bg-gray-50 text-xs">
                                    <span className="font-semibold text-gray-800 flex-1 min-w-[12rem]">{e.name}</span>
                                    <span className="text-gray-600 w-32">{e.framework}</span>
                                    <span className="text-gray-600 w-44">{showDate(e.periodFrom)} – {showDate(e.periodTo)}</span>
                                    <span className={`w-32 ${e.leadLeft ? "text-amber-700" : "text-gray-600"}`}>{e.leadName}{e.leadLeft ? " (left)" : ""}</span>
                                    <span className={`px-2 py-0.5 rounded-full border font-semibold ${STATUS_STYLE[e.status] || STATUS_STYLE.Planning}`}>{e.status}</span>
                                </button>
                                {openId === e.id && (
                                    <div className="px-3 pb-3 space-y-3" data-testid={`grc-audit-detail-${e.id}`}>
                                        <div className="text-xs text-gray-600">
                                            Scope: {e.scopeMode === "All" ? "every applicable requirement" : `${e.scopeRequirementIds.length} chosen requirement${e.scopeRequirementIds.length === 1 ? "" : "s"}`}
                                            {" · "}Due: {showDate(e.dueDate)}
                                        </div>
                                        <TeamSection engagement={e} members={peopleOf(e.id)} onChange={changeTeam(e.id)} />
                                        {e.framework === NERC_FRAMEWORK && <AuditCipSeniorManagers engagement={e} />}
                                        <AuditFieldwork key={`${e.id}-${e.status}`} engagement={e} onEngagementChanged={load}
                                            evidenceRequestsEnabled={evidenceRequestsEnabled} canViewRequirements={canViewRequirements} />
                                        <AuditReports key={`reports-${e.id}-${e.status}`} engagement={e} canArchive={canCreate} onEngagementChanged={load} />
                                        {e.canManage && (
                                            <div className="flex gap-2">
                                                {e.status !== "Closed" && (
                                                    <button type="button" onClick={() => setMode({ edit: e })}
                                                        className="text-xs px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-50">Edit</button>
                                                )}
                                                {e.status === "Planning" && (
                                                    <button type="button" onClick={() => remove(e)}
                                                        className="text-xs px-2 py-0.5 border border-red-200 rounded text-red-700 hover:bg-red-50">Delete</button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
        </div>
        </div>
    );
};

export default GrcAuditsTab;
